import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { HasAccess } from "@/components/common/has-access";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import BundleWizardStepper from "@/features/bundles/ui/wizard/bundle-wizard-stepper";
import RegionForm from "@/features/regions/ui/region-form";
import RegionCloneStep from "@/features/regions/ui/region-clone-step";
import { useCloneRegion } from "@/features/regions/hooks/use-clone-region";
import { Region } from "@/features/regions/types/region";

const STEPS = [
  { key: "region", label: "Region" },
  { key: "clone", label: "Clone products" },
];

export default function RegionCreateWizard() {
  const navigate = useNavigate();
  const clone = useCloneRegion();
  const [current, setCurrent] = useState(0);
  const [created, setCreated] = useState<Region | null>(null);
  const [sourceId, setSourceId] = useState("");
  const [priceOverrides, setPriceOverrides] = useState<Record<string, number>>(
    {},
  );
  const [productIds, setProductIds] = useState<string[]>([]);

  const goToDetail = () => {
    if (created) navigate(`../${created.id}`);
    else navigate("..");
  };

  const runClone = () => {
    if (!created || !sourceId) return;
    clone.mutate(
      {
        sourceRegionId: sourceId,
        targetRegionId: created.id,
        priceOverrides,
        productIds,
      },
      {
        onSuccess: (res) => {
          toast({
            title: "Products cloned",
            description: `${res.bundlesCreated} bundle(s), ${res.paymentPagesCreated} page(s), ${res.configsCreated} config(s) created.`,
          });
          goToDetail();
        },
        onError: (e: any) =>
          toast({
            title: "Error",
            description: e.response?.data?.message || "Failed to clone",
            variant: "destructive",
          }),
      },
    );
  };

  return (
    <HasAccess permission="write">
      <div className="max-w-2xl mx-auto p-6 space-y-5">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate("..")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-xl font-semibold">New region</h1>
        </div>

        <BundleWizardStepper steps={STEPS} current={current} />

        <Card>
          <CardContent className="pt-6">
            {current === 0 ? (
              // RegionForm has its own Create / Cancel buttons.
              <RegionForm
                editMode={false}
                onClose={() => navigate("..")}
                onCreated={(region) => {
                  setCreated(region);
                  setCurrent(1);
                }}
              />
            ) : (
              <div className="space-y-5">
                <p className="text-sm text-green-600">
                  Region “{created?.display_name}” created. Optionally clone an
                  existing region's products into it.
                </p>

                {created && (
                  <RegionCloneStep
                    targetRegionId={created.id}
                    sourceId={sourceId}
                    onSourceChange={setSourceId}
                    onPricesChange={setPriceOverrides}
                    onSelectionChange={setProductIds}
                  />
                )}

                <div className="flex justify-between pt-1">
                  <Button variant="outline" onClick={goToDetail}>
                    Skip
                  </Button>
                  <Button
                    onClick={runClone}
                    disabled={
                      !sourceId || productIds.length === 0 || clone.isPending
                    }
                  >
                    {clone.isPending ? "Cloning…" : "Clone & finish"}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </HasAccess>
  );
}
